import { useShow, useDelete } from '@refinedev/core'
import { Show } from '@refinedev/antd'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import {
  App,
  Card,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Spin,
  Result,
  List,
  Alert,
  Select,
  Form,
  Input,
  message,
  Tabs,
  Dropdown,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  CalendarOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  UserOutlined,
  FileOutlined,
  MedicineBoxOutlined,
  VideoCameraOutlined,
  MoreOutlined,
  HistoryOutlined,
} from '@ant-design/icons'

// Import tab components
import { OverviewTab, WorkflowsTab, AppointmentsTab, DocumentsTab } from '@/components/patients/tabs'
import { ScheduleConsultationModal } from '@/components/patients/ScheduleConsultationModal'

const { Text } = Typography
// NOTE: don't use `const { confirm } = Modal` here. In Ant Design v5,
// static Modal.confirm() renders via a detached portal that ignores the
// current <App> / <ConfigProvider> context — under Refine + React strict
// mode + our theme setup, it silently no-ops (the modal never mounts).
// We take `modal` from the App.useApp() hook instead, which is the
// officially recommended pattern and actually shows the dialog.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

// Workflow types
interface WorkflowStage {
  code: string
  name: string
  order: number
  icon: string | null
  color: string | null
  status: 'completed' | 'current' | 'pending'
  currentSubState?: string | null
}

interface WorkflowHistoryItem {
  id: string
  fromStateCode: string | null
  fromStateName: string | null
  toStateCode: string
  toStateName: string
  triggeredByRole: string
  triggeredByName: string | null
  reason: string | null
  createdAt: string
}

interface WorkflowDetails {
  instanceId: string | null
  label?: string | null
  workflowCode?: string
  workflowName?: string
  instanceStatus?: string
  startedAt?: string
  currentState: {
    id: string
    code: string
    name: string
    parentCode: string | null
    parentName: string | null
    icon: string | null
    color: string | null
    responsibleRole: string | null
    actionLabel: string | null
    isOverdue: boolean
    dueAt: string | null
    enteredAt: string
  } | null
  availableTransitions: {
    id: string
    toStateCode: string
    toStateName: string
    label: string | null
  }[]
  stages: WorkflowStage[]
  history: WorkflowHistoryItem[]
}

// Multi-instance workflow types
interface WorkflowInstanceSummary {
  id: string
  workflowCode: string
  workflowName: string
  label: string | null
  instanceStatus: string
  startedAt: string
  parentInstanceId: string | null
  parentInstanceLabel: string | null
  currentState: {
    id: string
    code: string
    name: string
    parentCode: string | null
    parentName: string | null
    isOverdue: boolean
    enteredAt: string
  } | null
  counts: {
    pendingTasks: number
    appointments: number
    labResults: number
  }
}

// Task types
interface TaskListItem {
  id: string
  taskType: string
  title: string
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'BLOCKED'
  dueAt: string | null
  assignedToRole: string | null
  createdAt: string
  isOverdue: boolean
  assignedTo: {
    id: string
    name: string
  } | null
  workflowInstance: {
    id: string
    label: string | null
    workflow: {
      code: string
      name: string
    }
  } | null
}

// Role labels
const roleLabels: Record<string, string> = {
  NURSE: 'Sestra',
  DOCTOR: 'Lekar',
  ADMIN: 'Admin',
  PATIENT: 'Pacijent',
  SYSTEM: 'Sistem',
  SUPPORT: 'Podrška',
}

// Priority labels and colors
const priorityConfig: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Nizak', color: 'default' },
  NORMAL: { label: 'Normalan', color: 'blue' },
  HIGH: { label: 'Visok', color: 'orange' },
  URGENT: { label: 'Hitan', color: 'red' },
}

// Task type labels
const taskTypeLabels: Record<string, string> = {
  VERIFY_LAB_RESULTS: 'Verifikuj rezultate',
  ASSIGN_DOCTOR: 'Dodeli lekara',
  REVIEW_LAB_RESULTS: 'Pregledaj rezultate',
  SCHEDULE_CONSULTATION: 'Zakaži konsultaciju',
  CONDUCT_CONSULTATION: 'Održi konsultaciju',
  ISSUE_PRESCRIPTION: 'Izdaj recept',
  COMPLETE_QUIZ: 'Popuni upitnik',
  ORDER_BLOOD_TEST: 'Naruči krvnu sliku',
  REQUEST_CONSULTATION: 'Zatraži konsultaciju',
  REVIEW_ESCALATION: 'Pregledaj eskalaciju',
  FOLLOW_UP_OVERDUE: 'Pratite prekoračeni rok',
  CUSTOM: 'Prilagođeno',
}

// Doctor interface for assignment
interface Doctor {
  id: string
  userId: string
  name: string
  specialty: string | null
}

interface Patient {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  dateOfBirth: string | null
  gender: 'MALE' | 'FEMALE' | 'OTHER' | null
  image: string | null
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED'
  createdAt: string
  updatedAt: string
  profile: {
    address: string | null
    city: string | null
    postalCode: string | null
    marketingConsent: boolean
    smsConsent: boolean
  } | null
  appointments?: Array<{
    id: string
    scheduledAt: string
    status: string
    type: string
    doctor?: {
      user: {
        firstName: string | null
        lastName: string | null
      }
    }
  }>
  labResults?: Array<{
    id: string
    labName: string
    sampleDate: string
    status: string
    reportUrl?: string | null
    documents?: Array<{
      id: string
      fileName: string
      fileUrl: string
      fileType: string
      fileSize: number | null
      createdAt: string
    }>
  }>
  orders?: Array<{
    id: string
    orderNumber: string
    total: number
    status: string
    createdAt: string
  }>
  quizResponses?: Array<{
    id: string
    quizType: string
    completed: boolean
    createdAt: string
  }>
}

const statusConfig: Record<Patient['status'], { color: string; text: string }> = {
  ACTIVE: { color: 'success', text: 'Aktivan' },
  SUSPENDED: { color: 'warning', text: 'Suspendovan' },
  DELETED: { color: 'default', text: 'Obrisan' },
}

export default function PatientShowPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // Scoped Modal instance — see the note next to the old
  // `const { confirm } = Modal` import about why the static API fails.
  const { modal } = App.useApp()

  const { query } = useShow<Patient>({
    resource: 'patients',
    id,
  })

  const deleteMutation = useDelete()
  const isDeleting = deleteMutation.mutation.status === 'pending'
  const { mutate: deletePatient } = deleteMutation

  const { data, isLoading, isError } = query || {}
  const patient = data?.data

  // Tab state
  const [activeTab, setActiveTab] = useState('overview')

  // Multi-instance workflow state
  const [workflowInstances, setWorkflowInstances] = useState<WorkflowInstanceSummary[]>([])
  const [instancesLoading, setInstancesLoading] = useState(false)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null)

  // Selected instance details (for expanded view)
  const [workflow, setWorkflow] = useState<WorkflowDetails | null>(null)
  const [workflowLoading, setWorkflowLoading] = useState(false)

  // Patient tasks state
  const [tasks, setTasks] = useState<TaskListItem[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)

  // Doctor assignment state
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [doctorsLoading, setDoctorsLoading] = useState(false)
  const [assignDoctorModalOpen, setAssignDoctorModalOpen] = useState(false)
  const [assigningDoctor, setAssigningDoctor] = useState(false)
  const [assignDoctorForm] = Form.useForm()

  // Doctor review state
  const [completeReviewModalOpen, setCompleteReviewModalOpen] = useState(false)
  const [completingReview, setCompletingReview] = useState(false)
  const [completeReviewForm] = Form.useForm()

  // Schedule consultation modal state
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)

  // Fetch workflow instances for patient
  const fetchWorkflowInstances = useCallback(async () => {
    if (!id) return
    setInstancesLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/patient/${id}/instances`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setWorkflowInstances(data)
        // Auto-select first instance if none selected
        if (data.length > 0 && !selectedInstanceId) {
          setSelectedInstanceId(data[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to fetch workflow instances:', error)
    } finally {
      setInstancesLoading(false)
    }
  }, [id, selectedInstanceId])

  // Fetch tasks for patient
  const fetchTasks = useCallback(async () => {
    if (!id) return
    setTasksLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/tasks/patient/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setTasks(data)
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      setTasksLoading(false)
    }
  }, [id])

  // Fetch available doctors
  const fetchDoctors = useCallback(async () => {
    setDoctorsLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/doctors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setDoctors(data)
      }
    } catch (error) {
      console.error('Failed to fetch doctors:', error)
    } finally {
      setDoctorsLoading(false)
    }
  }, [])

  // Fetch workflow details
  const fetchWorkflow = useCallback(async () => {
    if (!id) return
    setWorkflowLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/patient/${id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setWorkflow(data)
      }
    } catch (error) {
      console.error('Failed to fetch workflow:', error)
    } finally {
      setWorkflowLoading(false)
    }
  }, [id])

  // Load initial data: workflow instances and tasks
  useEffect(() => {
    fetchWorkflowInstances()
    fetchTasks()
  }, [fetchWorkflowInstances, fetchTasks])

  // Load details for selected instance
  useEffect(() => {
    if (selectedInstanceId) {
      fetchWorkflowDetails(selectedInstanceId)
    }
  }, [selectedInstanceId])

  // Fetch workflow details for a specific instance
  const fetchWorkflowDetails = async (instanceId: string) => {
    setWorkflowLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/instance/${instanceId}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setWorkflow(data)
      }
    } catch (error) {
      console.error('Failed to fetch workflow details:', error)
    } finally {
      setWorkflowLoading(false)
    }
  }

  // Fallback: fetch workflow if no instances (legacy behavior)
  useEffect(() => {
    if (workflowInstances.length === 0 && !instancesLoading) {
      fetchWorkflow()
    }
  }, [fetchWorkflow, workflowInstances.length, instancesLoading])

  // Fetch doctors when assign modal opens
  useEffect(() => {
    if (assignDoctorModalOpen) {
      fetchDoctors()
      assignDoctorForm.resetFields()
    }
  }, [assignDoctorModalOpen, fetchDoctors, assignDoctorForm])

  // Handle assign doctor submission
  const handleAssignDoctor = async (values: { doctorId: string; reason?: string }) => {
    if (!id) return

    setAssigningDoctor(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/assign-doctor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: id,
          doctorId: values.doctorId,
          reason: values.reason,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešno dodeljivanje lekara')
      }

      const updatedWorkflow = await response.json()
      setWorkflow(updatedWorkflow)
      setAssignDoctorModalOpen(false)
      message.success('Lekar uspešno dodeljen pacijentu')
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri dodeljivanju lekara')
    } finally {
      setAssigningDoctor(false)
    }
  }

  // Handle opening complete review modal
  const handleOpenCompleteReview = () => {
    completeReviewForm.resetFields()
    setCompleteReviewModalOpen(true)
  }

  // Handle complete review submission
  const handleCompleteReview = async (values: { reviewNotes: string }) => {
    if (!id) return

    setCompletingReview(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/complete-review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: id,
          reviewNotes: values.reviewNotes,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešno završavanje pregleda')
      }

      const updatedWorkflow = await response.json()
      setWorkflow(updatedWorkflow)
      setCompleteReviewModalOpen(false)
      message.success('Pregled rezultata uspešno završen')
      // Refresh data
      fetchWorkflowInstances()
      fetchTasks()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri završavanju pregleda')
    } finally {
      setCompletingReview(false)
    }
  }

  // Handle claiming a task
  const handleClaimTask = async (taskId: string) => {
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/tasks/${taskId}/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešno preuzimanje zadatka')
      }

      message.success('Zadatak preuzet')
      fetchTasks()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri preuzimanju zadatka')
    }
  }

  // Handle completing a task
  const handleCompleteTask = async (taskId: string) => {
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešno završavanje zadatka')
      }

      message.success('Zadatak završen')
      fetchTasks()
      fetchWorkflowInstances()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri završavanju zadatka')
    }
  }

  // Handle generic workflow transition
  const handleWorkflowTransition = async (toStateCode: string, label: string | null) => {
    if (!id) return

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: 'Potvrda akcije',
        icon: <ExclamationCircleFilled />,
        content: `Da li ste sigurni da želite da izvršite akciju: "${label || toStateCode}"?`,
        okText: 'Da',
        cancelText: 'Otkaži',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

    if (!confirmed) return

    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/workflow/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: id,
          toStateCode,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Neuspešna akcija')
      }

      const updatedWorkflow = await response.json()
      setWorkflow(updatedWorkflow)
      message.success('Akcija uspešno izvršena')
      // Refresh data
      fetchWorkflowInstances()
      fetchTasks()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Greška pri izvršavanju akcije')
    }
  }

  // Pending tasks (not completed/cancelled)
  const pendingTasks = tasks.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS')

  const handleDelete = () => {
    modal.confirm({
      title: 'Da li ste sigurni da želite da obrišete ovog pacijenta?',
      icon: <ExclamationCircleFilled />,
      content: 'Ova akcija će označiti pacijenta kao obrisanog. Podaci neće biti trajno uklonjeni.',
      okText: 'Da, obriši',
      okType: 'danger',
      cancelText: 'Otkaži',
      onOk() {
        deletePatient(
          { resource: 'patients', id: id! },
          {
            onSuccess: () => {
              navigate('/patients')
            },
          }
        )
      },
    })
  }

  // Actions dropdown menu
  const actionMenuItems: MenuProps['items'] = [
    {
      key: 'schedule-video',
      icon: <VideoCameraOutlined />,
      label: 'Zakaži video konsultaciju',
      onClick: () => setScheduleModalOpen(true),
    },
    {
      key: 'schedule-visit',
      icon: <CalendarOutlined />,
      label: 'Zakaži posetu',
      disabled: true, // TODO: Implement visit scheduling
    },
  ]

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isError || !patient) {
    return (
      <Result
        status="error"
        title="Greška"
        subTitle="Greška pri učitavanju podataka o pacijentu"
        extra={
          <Link to="/patients">
            <Button type="primary">Nazad na listu</Button>
          </Link>
        }
      />
    )
  }

  const patientName = patient.firstName || patient.lastName
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    : patient.email

  return (
    <Show
      title={patientName}
      headerButtons={
        <Space>
          <Dropdown menu={{ items: actionMenuItems }}>
            <Button icon={<MoreOutlined />}>Akcije</Button>
          </Dropdown>
          <Link to={`/patients/${id}/edit`}>
            <Button icon={<EditOutlined />}>Izmeni</Button>
          </Link>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            loading={isDeleting}
          >
            Obriši
          </Button>
        </Space>
      }
    >
      {/* Status Badge */}
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Text type="secondary">{patient.email}</Text>
          <Tag color={statusConfig[patient.status].color}>
            {statusConfig[patient.status].text}
          </Tag>
        </Space>
      </div>

      {/* Immediate Actions - Pending Tasks */}
      {pendingTasks.length > 0 && (
        <Card
          title={
            <Space>
              <ExclamationCircleFilled style={{ color: '#faad14' }} />
              <span>Hitne akcije ({pendingTasks.length})</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
          loading={tasksLoading}
        >
          <List
            size="small"
            dataSource={pendingTasks}
            renderItem={(task) => (
              <List.Item
                actions={[
                  task.status === 'PENDING' ? (
                    <Button
                      key="claim"
                      type="primary"
                      size="small"
                      onClick={() => handleClaimTask(task.id)}
                    >
                      Preuzmi
                    </Button>
                  ) : (
                    <Button
                      key="complete"
                      type="primary"
                      size="small"
                      onClick={() => handleCompleteTask(task.id)}
                    >
                      Završi
                    </Button>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={priorityConfig[task.priority]?.color || 'default'}>
                        {priorityConfig[task.priority]?.label || task.priority}
                      </Tag>
                      <Text strong>{task.title}</Text>
                      {task.isOverdue && (
                        <Tag color="error">Zakasnelo</Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space size={4} split={<Text type="secondary">•</Text>}>
                      <Text type="secondary">
                        {taskTypeLabels[task.taskType] || task.taskType}
                      </Text>
                      {task.workflowInstance && (
                        <Text type="secondary">
                          {task.workflowInstance.label || task.workflowInstance.workflow.name}
                        </Text>
                      )}
                      {task.assignedTo && (
                        <Text type="secondary">
                          Dodeljeno: {task.assignedTo.name}
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Workflow Actions for current state */}
      {workflow?.instanceId && workflow.currentState && (
        <Card
          title={
            <Space>
              <MedicineBoxOutlined />
              <span>Dostupne akcije</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          {/* Check if current state is AWAITING_NURSE_ASSIGNMENT - direct to lab results for verification */}
          {workflow.currentState.code === 'TRT_AWAITING_NURSE_ASSIGNMENT' && (() => {
            const pendingLabResult = patient?.labResults?.find(
              lr => lr.status === 'RECEIVED' || lr.status === 'PENDING'
            )
            const labResultUrl = pendingLabResult
              ? `/lab-results/${pendingLabResult.id}`
              : '/lab-results?status=RECEIVED'

            return (
              <Alert
                message="Verifikuj lab rezultate"
                description="Pacijent je otpremio rezultate laboratorije. Potrebno je pregledati dokument, uneti biomarkere i dodeliti lekara."
                type="warning"
                showIcon
                icon={<ExperimentOutlined />}
                action={
                  <Link to={labResultUrl}>
                    <Button
                      type="primary"
                      icon={<ExperimentOutlined />}
                    >
                      {pendingLabResult ? 'Verifikuj rezultate' : 'Idi na lab rezultate'}
                    </Button>
                  </Link>
                }
                style={{ marginBottom: 16 }}
              />
            )
          })()}

          {/* Check if current state is AWAITING_DOCTOR_REVIEW - show complete review button */}
          {workflow.currentState.code === 'TRT_AWAITING_DOCTOR_REVIEW' && (
            <Alert
              message="Pregledajte rezultate"
              description="Rezultati laboratorije su dodeljeni za pregled. Pregledajte rezultate i unesite svoje mišljenje."
              type="warning"
              showIcon
              icon={<MedicineBoxOutlined />}
              action={
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleOpenCompleteReview}
                >
                  Završi pregled
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Check if current state is CONSULTATION_REQUESTED - show schedule consultation button */}
          {workflow.currentState.code === 'TRT_CONSULTATION_REQUESTED' && (
            <Alert
              message="Zakaži konsultaciju"
              description="Pacijent je zatražio konsultaciju sa lekarom. Potrebno je kontaktirati pacijenta telefonom i zakazati termin."
              type="warning"
              showIcon
              icon={<CalendarOutlined />}
              action={
                <Link to={`/consultations/${workflow.currentState.id}`}>
                  <Button
                    type="primary"
                    icon={<CalendarOutlined />}
                  >
                    Zakaži termin
                  </Button>
                </Link>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Show available transitions */}
          {workflow.availableTransitions.length > 0 && (
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Dostupne tranzicije:
              </Text>
              <Space wrap>
                {workflow.availableTransitions
                  .filter(t => t.toStateCode !== 'TRT_AWAITING_DOCTOR_REVIEW')
                  .map(transition => (
                    <Button
                      key={transition.id}
                      onClick={() => handleWorkflowTransition(transition.toStateCode, transition.label)}
                    >
                      {transition.label || transition.toStateName}
                    </Button>
                  ))}
              </Space>
            </div>
          )}

          {workflow.availableTransitions.length === 0 &&
           workflow.currentState.code !== 'TRT_AWAITING_NURSE_ASSIGNMENT' &&
           workflow.currentState.code !== 'TRT_AWAITING_DOCTOR_REVIEW' &&
           workflow.currentState.code !== 'TRT_CONSULTATION_REQUESTED' && (
            <Alert
              message="Nema dostupnih akcija"
              description={`Trenutno stanje "${workflow.currentState.name}" ne dozvoljava automatske tranzicije. Potrebna je akcija od strane ${roleLabels[workflow.currentState.responsibleRole || ''] || 'korisnika'}.`}
              type="info"
              showIcon
            />
          )}
        </Card>
      )}

      {/* Tabbed Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'overview',
            label: <Space><UserOutlined /> Pregled</Space>,
            children: <OverviewTab patient={patient} />,
          },
          {
            key: 'workflows',
            label: <Space><SyncOutlined /> Workflow-i ({workflowInstances.length})</Space>,
            children: (
              <WorkflowsTab
                workflowInstances={workflowInstances}
                selectedInstanceId={selectedInstanceId}
                onSelectInstance={setSelectedInstanceId}
                workflowDetails={workflow}
                loading={instancesLoading}
                detailsLoading={workflowLoading}
              />
            ),
          },
          {
            key: 'appointments',
            label: <Space><CalendarOutlined /> Termini</Space>,
            children: <AppointmentsTab patientId={id!} />,
          },
          {
            key: 'documents',
            label: <Space><FileOutlined /> Dokumenti</Space>,
            children: <DocumentsTab labResults={patient.labResults} />,
          },
        ]}
      />

      {/* Assign Doctor Modal */}
      <Modal
        title="Dodeli lekara pacijentu"
        open={assignDoctorModalOpen}
        onCancel={() => setAssignDoctorModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={assignDoctorForm}
          layout="vertical"
          onFinish={handleAssignDoctor}
        >
          <Form.Item
            name="doctorId"
            label="Izaberi lekara"
            rules={[{ required: true, message: 'Molimo izaberite lekara' }]}
          >
            <Select
              placeholder="Izaberi lekara..."
              loading={doctorsLoading}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={doctors.map(doctor => ({
                value: doctor.id,
                label: `${doctor.name}${doctor.specialty ? ` (${doctor.specialty})` : ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Napomena (opciono)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Dodaj napomenu za dodeljivanje..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setAssignDoctorModalOpen(false)}>
                Otkaži
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={assigningDoctor}
                icon={<MedicineBoxOutlined />}
              >
                Dodeli lekara
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Complete Review Modal */}
      <Modal
        title="Završi pregled rezultata"
        open={completeReviewModalOpen}
        onCancel={() => setCompleteReviewModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={completeReviewForm}
          layout="vertical"
          onFinish={handleCompleteReview}
        >
          <Form.Item
            name="reviewNotes"
            label="Mišljenje lekara"
            rules={[{ required: true, message: 'Molimo unesite mišljenje o rezultatima' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Npr: Rezultati su u granicama normale. Pacijent može nastaviti sa konsultacijom..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCompleteReviewModalOpen(false)}>
                Otkaži
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={completingReview}
                icon={<CheckCircleOutlined />}
              >
                Završi pregled
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Schedule Consultation Modal */}
      <ScheduleConsultationModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSuccess={() => {
          fetchWorkflowInstances()
          // Refresh appointments tab if we're on it
          if (activeTab === 'appointments') {
            // The AppointmentsTab will refetch on mount
          }
        }}
        patient={{
          id: patient.id,
          email: patient.email,
          firstName: patient.firstName,
          lastName: patient.lastName,
          phone: patient.phone,
          image: patient.image,
          name: patientName,
        }}
      />
    </Show>
  )
}
